import { useCallback, useReducer, useRef } from 'react';
import { t } from '@lingui/macro';

import { MAX_RESULTS } from '../constants';
import { searchByName } from '../mockApi';
import type { SearchByNameAction, SearchByNameState } from '../types';

const initialState: SearchByNameState = {
  query: '',
  results: null,
  isLoading: false,
  error: null,
  wasCapped: false,
};

// Localized at call-time so Lingui picks up the active locale; do not hoist.
const getGenericSearchError = (): string => t`Something went wrong. Please try again.`;

function reducer(state: SearchByNameState, action: SearchByNameAction): SearchByNameState {
  switch (action.type) {
    case 'SEARCH_START':
      return {
        ...state,
        query: action.query,
        isLoading: true,
        error: null,
        wasCapped: false,
      };
    case 'SEARCH_SUCCESS':
      return {
        ...state,
        results: action.results,
        isLoading: false,
        wasCapped: action.wasCapped,
      };
    case 'SEARCH_ERROR':
      return { ...state, error: action.error, isLoading: false };
    default:
      return state;
  }
}

type UseSearchByNameReturn = {
  state: SearchByNameState;

  search: (name: string, location: string) => Promise<void>;
};

export default function useSearchByName(): UseSearchByNameReturn {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Drop out-of-order responses when searches overlap.
  const requestIdRef = useRef(0);

  const search = useCallback(async (name: string, location: string): Promise<void> => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    dispatch({ type: 'SEARCH_START', query: name });
    try {
      const raw = await searchByName(name, location);
      if (requestIdRef.current !== requestId) return;
      const wasCapped = raw.length > MAX_RESULTS;
      const results = wasCapped ? raw.slice(0, MAX_RESULTS) : raw;
      dispatch({ type: 'SEARCH_SUCCESS', results, wasCapped });
    } catch {
      if (requestIdRef.current !== requestId) return;
      dispatch({ type: 'SEARCH_ERROR', error: getGenericSearchError() });
    }
  }, []);

  return { state, search };
}
